/**
 * gp-logcat 日志记录插件类型定义
 * @description UniApp Android 日志记录工具，支持本地文件存储和控制台输出
 * @version 1.0.0
 */

/**
 * 日志级别枚举
 */
export enum LogLevel {
    /** 调试日志 */
    DEBUG = 'DEBUG',
    /** 信息日志 */
    INFO = 'INFO',
    /** 警告日志 */
    WARN = 'WARN',
    /** 错误日志 */
    ERROR = 'ERROR'
}

/**
 * 日志级别优先级（数值越大优先级越高）
 */
export const LogLevelPriority: Record<LogLevel, number> = {
    [LogLevel.DEBUG]: 0,
    [LogLevel.INFO]: 1,
    [LogLevel.WARN]: 2,
    [LogLevel.ERROR]: 3
};

/**
 * 日志条目接口
 */
export interface LogEntry {
    /** 日志时间戳 */
    timestamp: string;
    /** 日志级别 */
    level: LogLevel;
    /** 日志消息 */
    message: string;
    /** 日志标签 */
    tag?: string;
    /** 附加数据 */
    data?: unknown;
}

/**
 * 日志初始化配置项
 */
export interface LogCatOptions {
    /**
     * 日志保存天数
     * @default 14
     */
    saveDays?: number;

    /**
     * 日志文件名前缀
     * @default 'log'
     */
    filePrefix?: string;

    /**
     * 日志文件扩展名
     * @default '.txt'
     */
    fileExtension?: string;

    /**
     * 是否启用控制台输出
     * @default true
     */
    enableConsole?: boolean;

    /**
     * 是否启用文件写入
     * @default true
     */
    enableFileWrite?: boolean;

    /**
     * 最低日志级别（低于此级别的日志将被忽略）
     * @default LogLevel.DEBUG
     */
    minLevel?: LogLevel;

    /**
     * 日志标签（用于区分不同模块）
     * @default 'APP'
     */
    tag?: string;

    /**
     * 自定义日志目录名称（相对于应用文件目录）
     * @default 'logs'
     */
    logDirName?: string;

    /**
     * 单个日志文件最大大小（字节），超过后自动分片
     * @default 5242880 (5MB)
     */
    maxFileSize?: number;

    /**
     * 是否在初始化时清理过期日志
     * @default true
     */
    cleanOnInit?: boolean;

    /**
     * 日期格式化函数（用于自定义日期显示格式）
     */
    dateFormatter?: (date: Date) => string;

    /**
     * 时间格式化函数（用于自定义时间显示格式）
     */
    dateTimeFormatter?: (date: Date) => string;
}

/**
 * 日志统计信息
 */
export interface LogStatistics {
    /** 日志存储路径 */
    logPath: string;
    /** 日志文件数量 */
    fileCount: number;
    /** 总文件大小（字节） */
    totalSize: number;
    /** 最早的日志文件日期 */
    oldestLogDate?: string;
    /** 最新的日志文件日期 */
    newestLogDate?: string;
    /** 日志文件列表 */
    files: LogFileInfo[];
}

/**
 * 日志文件信息
 */
export interface LogFileInfo {
    /** 文件名 */
    name: string;
    /** 文件路径 */
    path: string;
    /** 文件大小（字节） */
    size: number;
    /** 文件日期 */
    date: string;
    /** 最后修改时间 */
    lastModified: number;
}

/**
 * Android 原生类引用类型
 */
export interface AndroidNativeClasses {
    /** MainActivity 实例 */
    main: any;
    /** android.os.Environment 类 */
    Environment: any;
    /** java.io.BufferedWriter 类 */
    BufferedWriter: any;
    /** java.io.File 类 */
    File: any;
    /** java.io.FileOutputStream 类 */
    FileOutputStream: any;
    /** java.io.OutputStreamWriter 类 */
    OutputStreamWriter: any;
    /** java.io.FileReader 类 */
    FileReader: any;
    /** java.io.BufferedReader 类 */
    BufferedReader: any;
}

/**
 * 日志管理器状态
 */
export interface LogCatState {
    /** 是否已初始化 */
    isInitialized: boolean;
    /** 日志存储路径 */
    logPath: string;
    /** 当前配置 */
    options: Required<LogCatOptions>;
}

/**
 * 日志写入回调函数
 */
export type LogWriteCallback = (entry: LogEntry, success: boolean) => void;

/**
 * 日志管理器接口
 */
export interface ILogCat {
    /**
     * 初始化日志系统
     * @param options 初始化配置
     * @returns 是否初始化成功
     */
    init(options?: LogCatOptions): boolean;

    /**
     * 销毁日志系统，释放资源
     */
    destroy(): void;

    /**
     * 获取当前状态
     */
    getState(): LogCatState;

    /**
     * 获取日志统计信息
     */
    getStatistics(): LogStatistics | null;

    /**
     * 清理过期日志
     * @returns 清理的文件数量
     */
    cleanOldLogs(): number;

    /**
     * 清理所有日志
     * @returns 清理的文件数量
     */
    cleanAllLogs(): number;

    /**
     * 读取指定日期的日志内容
     * @param date 日期（YYYY-MM-DD 格式）
     * @returns 日志内容
     */
    readLog(date: string): string | null;

    /**
     * 读取今天的日志内容
     * @returns 日志内容
     */
    readTodayLog(): string | null;

    /**
     * 导出日志文件
     * @param dates 日期列表（YYYY-MM-DD 格式），不传则导出全部
     * @returns 日志文件路径列表
     */
    exportLogs(dates?: string[]): string[];

    /**
     * 写入日志
     * @param level 日志级别
     * @param message 日志消息
     * @param data 附加数据
     */
    writeLog(level: LogLevel, message: string, data?: unknown): void;

    /**
     * DEBUG 级别日志
     * @param message 日志消息
     * @param data 附加数据
     */
    debug(message: string, data?: unknown): void;

    /**
     * INFO 级别日志
     * @param message 日志消息
     * @param data 附加数据
     */
    info(message: string, data?: unknown): void;

    /**
     * WARN 级别日志
     * @param message 日志消息
     * @param data 附加数据
     */
    warn(message: string, data?: unknown): void;

    /**
     * ERROR 级别日志
     * @param message 日志消息
     * @param data 附加数据
     */
    error(message: string, data?: unknown): void;

    /**
     * 带标签的日志
     * @param tag 标签
     * @param level 日志级别
     * @param message 日志消息
     * @param data 附加数据
     */
    tag(tag: string, level: LogLevel, message: string, data?: unknown): void;

    /**
     * 设置日志写入回调
     * @param callback 回调函数
     */
    setWriteCallback(callback: LogWriteCallback | null): void;

    /**
     * 更新配置（部分更新）
     * @param options 配置项
     */
    updateOptions(options: Partial<LogCatOptions>): void;

    /**
     * 格式化日期 YYYY-MM-DD
     * @param date 日期对象
     */
    formatDate(date: Date): string;

    /**
     * 格式化日期时间 YYYY-MM-DD HH:mm:ss
     * @param date 日期对象
     */
    formatDateTime(date: Date): string;
}

/**
 * 创建日志管理器工厂函数类型
 */
export type CreateLogCatFn = (options?: LogCatOptions) => ILogCat;
